import React, { useEffect, useState } from 'react';

import { Field, Formik } from 'formik';
import * as Yup from 'yup';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import ContentList from 'CommonComponents/ContentList';
import SectionTile from 'CommonComponents/SectionTile';
import TextButton from 'CommonComponents/TextButton';

import { useToast } from 'Hooks/useToast';

const PERCENT = [
    'cashflow_amber_variance_pct',
    'workforce_red_overdue_pct',
    'contracts_variation_tolerance_pct',
    'plans_red_activities_overdue_pct',
    'plans_amber_activities_overdue_pct',
    'departments_budget_tolerance_pct',
];

const DAYS = [
    'contracts_red_overdue_days',
    'issues_red_open_days',
    'decisions_amber_due_within_days',
    'plans_red_key_overdue_days',
    'plans_red_key_projected_late_days',
];

/**
 * Each area's rule as a sentence, with its thresholds as inline inputs: a
 * string is text, an object is a number input (`wide` for a money value).
 * Procurements, risks and the roadmap have no thresholds and show their fixed
 * wording.
 */
const ROWS = () => [
    {
        title: $t('Cashflow'),
        parts: [
            $t(
                'Red if the cash position is below zero or the forecast closing balance goes negative within the forecast horizon; amber if the cumulative variance, excluding VAT/GST, is more than',
            ),
            { name: 'cashflow_amber_variance_pct', unit: '%' },
            $t('of forecast outflows to date.'),
        ],
    },
    {
        title: $t('Workforce'),
        parts: [
            $t('Red if overdue positions exceed'),
            { name: 'workforce_red_overdue_pct', unit: '%' },
            $t('of positions due to date; amber if any are overdue.'),
        ],
    },
    { title: $t('Procurements'), parts: [$t('Red if any high-priority procurement is overdue; amber if any is overdue.')] },
    {
        title: $t('Contract management'),
        parts: [
            $t('Red if any milestone on a contract over'),
            { name: 'contracts_value_threshold', unit: '', wide: true },
            $t('(0 for any contract) is overdue by more than'),
            { name: 'contracts_red_overdue_days', unit: $t('days') },
            $t(', or variations exceed'),
            { name: 'contracts_variation_tolerance_pct', unit: '%' },
            $t('of contracted value; amber if any milestone is overdue.'),
        ],
    },
    { title: $t('Risks'), parts: [$t('Red if any open risk is red post-treatment; amber if any is amber post-treatment.')] },
    {
        title: $t('Issues'),
        parts: [
            $t('Red if any high issue has been open for more than'),
            { name: 'issues_red_open_days', unit: $t('days') },
            $t('; amber if any high issue is open.'),
        ],
    },
    {
        title: $t('Decisions'),
        parts: [$t('Red if any decision required is past due; amber if any is due within'), { name: 'decisions_amber_due_within_days', unit: $t('days') }, '.'],
    },
    {
        title: $t('Milestones and plans'),
        parts: [
            $t('Red if any key milestone is overdue by more than'),
            { name: 'plans_red_key_overdue_days', unit: $t('days') },
            $t(', more than'),
            { name: 'plans_red_activities_overdue_pct', unit: '%' },
            $t('of activities are overdue, or a key milestone is projected more than'),
            { name: 'plans_red_key_projected_late_days', unit: $t('days') },
            $t('late. Amber if any key milestone is overdue or projected late, or at least'),
            { name: 'plans_amber_activities_overdue_pct', unit: '%' },
            $t('of activities are overdue.'),
        ],
    },
    { title: $t('Roadmap'), parts: [$t('Not configured; can be set manually.')] },
    {
        title: $t('Departments'),
        parts: [
            $t('The worst of budget, procurements, workforce, activities and risks. Budget is red if AFC exceeds budget by more than'),
            { name: 'departments_budget_tolerance_pct', unit: '%' },
            $t(', and amber if LE exceeds budget.'),
        ],
    },
];

const schema = () => {
    const shape = {
        contracts_value_threshold: Yup.number().typeError($t('Enter a number')).required($t('Required')).min(0, $t('Cannot be negative')),
    };

    PERCENT.forEach((name) => {
        shape[name] = Yup.number().typeError($t('Enter a number')).required($t('Required')).min(0, $t('Between 0 and 100')).max(100, $t('Between 0 and 100'));
    });

    DAYS.forEach((name) => {
        shape[name] = Yup.number()
            .typeError($t('Enter a number'))
            .required($t('Required'))
            .integer($t('Whole days only'))
            .min(0, $t('Between 0 and 3650'))
            .max(3650, $t('Between 0 and 3650'));
    });

    shape.plans_amber_activities_overdue_pct = shape.plans_amber_activities_overdue_pct.test(
        'amber-below-red',
        $t('The amber share of overdue activities cannot be above the red share.'),
        function (value) {
            const red = Number(this.parent.plans_red_activities_overdue_pct);

            return value === undefined || Number.isNaN(red) || Number(value) <= red;
        },
    );

    return Yup.object().shape(shape);
};

const FIELD_LABELS = () => ({
    cashflow_amber_variance_pct: $t('Cashflow amber share of forecast outflows'),
    workforce_red_overdue_pct: $t('Workforce red share of overdue positions'),
    contracts_value_threshold: $t('Contract value threshold'),
    contracts_red_overdue_days: $t('Contract milestone days overdue for red'),
    contracts_variation_tolerance_pct: $t('Contract variation tolerance'),
    issues_red_open_days: $t('Days a high issue is open for red'),
    decisions_amber_due_within_days: $t('Days ahead a decision is due for amber'),
    plans_red_key_overdue_days: $t('Key milestone days overdue for red'),
    plans_red_activities_overdue_pct: $t('Red share of overdue activities'),
    plans_red_key_projected_late_days: $t('Key milestone days projected late for red'),
    plans_amber_activities_overdue_pct: $t('Amber share of overdue activities'),
    departments_budget_tolerance_pct: $t('Department budget tolerance'),
});

// Text after an input starts with a space, unless it starts with punctuation,
// so the sentence reads "14 days, or ..." rather than "14 days , or ...".
const spaceBefore = (text, index) => (index === 0 || /^[,;.]/.test(text) ? '' : ' ');

const firstErrorFor = (errors, names) => names.map((name) => errors[name]).find(Boolean);

/**
 * One rule across the full width of the card: the area on the left, its
 * sentence with the inputs inline on the right. Stacks on narrow screens.
 */
function RuleRow({ title, children }) {
    return (
        <li className="tw-flex max-lg:tw-flex-col tw-gap-x-4">
            <span className="tw-w-3/12 max-lg:tw-w-full tw-font-semibold">{title}</span>
            <span className="tw-w-9/12 max-lg:tw-w-full">{children}</span>
        </li>
    );
}

/**
 * The project's management pack ratings: the thresholds that turn each
 * area of the pack red, amber or green. Draft packs apply them on their next
 * preview; issued packs keep the rules they were issued with.
 *
 * Its own endpoint and Save, like the time lock: only somebody holding
 * manage-settings on the project may change them.
 */
export default function StatusRulesCard({ url }) {
    const [rules, setRules] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [collapsed, setCollapsed] = useState(true);
    const [error, setError] = useState(null);

    const toast = useToast();

    const failure = (e) =>
        e.response?.status === 403
            ? $t('You do not have permission to change the management pack ratings')
            : e.response?.data?.message || $t('Something went wrong');

    useEffect(() => {
        axios
            .get(url)
            .then(({ data }) => {
                setRules(data.data?.rules ?? null);
                setDefaults(data.data?.defaults ?? null);
            })
            .catch((e) => setError(failure(e)));
    }, [url]);

    const save = (values, { setSubmitting, setErrors }) => {
        setError(null);

        axios
            .put(url, values)
            .then(({ data }) => {
                setRules(data.data?.rules ?? values);
                toast.success($t('The management pack ratings were saved'));
            })
            .catch((e) => {
                if (e.response?.status === 422 && e.response.data?.errors) {
                    setErrors(Object.fromEntries(Object.entries(e.response.data.errors).map(([name, messages]) => [name, messages[0]])));
                }

                setError(failure(e));
            })
            .finally(() => setSubmitting(false));
    };

    return (
        <SectionTile collapsed={collapsed} header={$t('Management pack ratings')} onToggle={() => setCollapsed((previous) => !previous)}>
            {!rules && !error && <p className="tw-text-sm tw-text-charcoal-75 tw-m-0">{$t('Loading...')}</p>}

            {rules && (
                <Formik initialValues={rules} enableReinitialize validationSchema={schema()} onSubmit={save}>
                    {({ errors, handleSubmit, isSubmitting, setValues }) => (
                        <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-flex-auto" noValidate>
                            <p className="tw-text-sm tw-text-charcoal-75 tw-m-0 tw-mb-2">
                                {$t('How each area of the monthly management pack is rated. Every area is green unless a rule below applies.')}
                            </p>
                            <ContentList>
                                {ROWS().map((row) => {
                                    const fields = row.parts.filter((part) => typeof part === 'object').map((part) => part.name);
                                    const rowError = firstErrorFor(errors, fields);

                                    return (
                                        <RuleRow key={row.title} title={row.title}>
                                            <div className="tw-flex tw-flex-col tw-gap-1">
                                                <p className="tw-text-sm tw-text-charcoal tw-m-0 tw-leading-8">
                                                    {row.parts.map((part, index) =>
                                                        typeof part === 'string' ? (
                                                            <span key={index}>{`${spaceBefore(part, index)}${part}`}</span>
                                                        ) : (
                                                            <span key={part.name} className="tw-inline-flex tw-items-center tw-gap-1 tw-ms-1">
                                                                <Field
                                                                    name={part.name}
                                                                    type="number"
                                                                    min={0}
                                                                    step="any"
                                                                    aria-label={FIELD_LABELS()[part.name]}
                                                                    className={`text-input tw-border-0 tw-h-8 !tw-px-2 ${part.wide ? '!tw-w-28' : '!tw-w-16'}`}
                                                                    disabled={isSubmitting}
                                                                />
                                                                {part.unit}
                                                            </span>
                                                        ),
                                                    )}
                                                </p>
                                                {!!rowError && <span className="tw-text-red-500 tw-text-sm">{rowError}</span>}
                                            </div>
                                        </RuleRow>
                                    );
                                })}
                            </ContentList>

                            {!!error && <Alert type="error">{error}</Alert>}

                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-3 tw-mt-auto tw-pt-3">
                                {defaults && (
                                    <TextButton onClick={() => setValues(defaults)} disabled={isSubmitting}>
                                        {$t('Reset to defaults')}
                                    </TextButton>
                                )}
                                <Button variant="primary" type="submit" disabled={isSubmitting}>
                                    {$t('Save')}
                                </Button>
                            </div>
                        </form>
                    )}
                </Formik>
            )}

            {!rules && !!error && <Alert type="error">{error}</Alert>}
        </SectionTile>
    );
}
